// src/pages/ShopifyWebhookEvents.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiGet } from "../../api/client";
import { A, pillClass } from "../../styles/appleClasses";
import { Button } from "../../components/Button";
import SectionShell, {
  type SectionTab,
} from "../../components/layout/SectionShell";
import ShopifyBillingRequiredPanel from "../../components/shopify/ShopifyBillingRequiredPanel";
import type { ShopifyStatus } from "../../types/shopify";
import { isShopifyBillingRequired } from "../../types/shopify";

type WebhookEvent = {
  id: number;
  topic: string;
  shop_domain: string;
  status: string;
  received_at?: string | null;
  processed_at?: string | null;
  error_message?: string | null;
  retry_count?: number;
};

const SHOPIFY_TABS: SectionTab[] = [
  { label: "Overview", to: "/commerce/shopify", end: true },
  { label: "Product Mapping", to: "/commerce/shopify/product-mapping" },
  { label: "Sync Issues", to: "/commerce/shopify/sync-issues" },
  { label: "Activity", to: "/commerce/shopify/diagnostics" },
];

function shortDate(value?: string | null) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function titleize(value?: string | null) {
  if (!value) return "Unknown";

  return value
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function eventTone(status?: string | null) {
  const s = (status || "").toLowerCase();

  if (s.includes("processed") || s === "imported" || s === "auto_ready") {
    return "success";
  }

  if (s.includes("failed") || s.includes("review") || s.includes("error")) {
    return "warning";
  }

  if (s.includes("ignored")) return "neutral";

  return "info";
}

function statusRows(status: ShopifyStatus | null) {
  if (!status) return [];

  return [
    ["connected", String(status.connected)],
    ["shop_domain", status.shop_domain || "—"],
    ["status", status.status || "—"],
    ["shopify_connector_active", String(!!status.shopify_connector_active)],
    ["can_use_shopify", String(!!status.can_use_shopify)],
  ];
}

export default function ShopifyWebhookEvents() {
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<ShopifyStatus | null>(null);
  const [events, setEvents] = useState<WebhookEvent[]>([]);

  const shopifyBillingRequired = isShopifyBillingRequired(status);

  const params = new URLSearchParams(location.search);
  const eventId = params.get("event_id") || "";
  const query = params.get("q") || eventId;
  const latestEvent = events[0] || null;

  async function refresh() {
    setBusy(true);
    setErr(null);

    try {
      const statusRes = await apiGet<ShopifyStatus>("/shopify/status");
      setStatus(statusRes);

      if (isShopifyBillingRequired(statusRes)) {
        setEvents([]);
        return;
      }

      const eventRows = await apiGet<WebhookEvent[]>("/shopify/webhook-events");
      setEvents(eventRows || []);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to load Shopify diagnostics.",
      );
    } finally {
      setLoading(false);
      setBusy(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return events;

    return events.filter((ev) =>
      [ev.id, ev.topic, ev.shop_domain, ev.status, ev.error_message]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [events, query]);

  if (shopifyBillingRequired) {
    return (
      <SectionShell
        title="Shopify"
        tabs={SHOPIFY_TABS}
        tabsAriaLabel="Shopify sections"
        closePath="/commerce/shopify"
      >
        <main className="mx-auto w-full max-w-3xl">
          <ShopifyBillingRequiredPanel
            installedAppUrl={status?.installed_app_url}
            shopifyAdminAppUrl={status?.shopify_admin_app_url}
          />
        </main>
      </SectionShell>
    );
  }

  return (
    <SectionShell
      title="Shopify"
      tabs={SHOPIFY_TABS}
      tabsAriaLabel="Shopify sections"
      closePath="/commerce/shopify"
    >
      <main className="mx-auto w-full max-w-3xl">
        <div className={`${A.card} min-w-0 overflow-hidden p-4 sm:p-5`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className={A.title}>Diagnostics</h2>
              <p className={`${A.label} mt-1`}>
                Technical Shopify connection and webhook details for review.
              </p>
            </div>

            <Button
              type="button"
              variant="secondary"
              onClick={refresh}
              disabled={busy}
              minWidth="auto"
              className="px-3"
            >
              {busy ? "Refreshing…" : "Refresh"}
            </Button>
          </div>

          {err ? (
            <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
              {err}
            </div>
          ) : null}

          <div className="mt-4 min-w-0 max-w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
            <div className={A.label}>Last webhook received</div>
            <div className="mt-1 min-w-0 break-words text-[13px] font-medium text-gray-800">
              {loading ? "Loading…" : shortDate(latestEvent?.received_at)}
            </div>
          </div>

          <div className="mt-4 min-w-0 max-w-full overflow-hidden rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="text-[13px] font-semibold text-gray-800">
              Connection object
            </div>

            <div className="mt-3 grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
              {statusRows(status).map(([label, value]) => (
                <div
                  key={label}
                  className="min-w-0 max-w-full rounded-xl border border-gray-100 bg-white px-3 py-2"
                >
                  <div className={`${A.label} min-w-0 break-words`}>
                    {label}
                  </div>
                  <div className="mt-1 min-w-0 break-all text-[13px] font-medium text-gray-700">
                    {loading ? "Loading…" : value}
                  </div>
                </div>
              ))}

              {!loading && !status ? (
                <div className="min-w-0 max-w-full break-words rounded-xl border border-gray-100 bg-white px-3 py-2 text-[13px] text-gray-500">
                  No Shopify status object returned.
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-4 text-xs text-gray-500">
            Showing {loading ? "—" : filtered.length} of{" "}
            {loading ? "—" : events.length} webhook events.
          </div>

          <div className="mt-4 space-y-3">
            {filtered.map((event) => (
              <div
                key={event.id}
                className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-0 break-words text-[13px] font-semibold text-gray-700">
                        {event.topic}
                      </div>
                      <span className={pillClass(eventTone(event.status))}>
                        {titleize(event.status || "received")}
                      </span>
                    </div>

                    <div className="mt-1 min-w-0 break-words text-xs text-gray-500">
                      Event #{event.id} · {event.shop_domain}
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="min-w-0 max-w-full rounded-xl border border-gray-100 bg-white px-3 py-2">
                    <div className={A.label}>Received</div>
                    <div className="mt-1 min-w-0 break-words text-[13px] font-medium text-gray-700">
                      {shortDate(event.received_at)}
                    </div>
                  </div>

                  <div className="min-w-0 max-w-full rounded-xl border border-gray-100 bg-white px-3 py-2">
                    <div className={A.label}>Processed</div>
                    <div className="mt-1 min-w-0 break-words text-[13px] font-medium text-gray-700">
                      {shortDate(event.processed_at)}
                    </div>
                  </div>

                  <div className="min-w-0 max-w-full rounded-xl border border-gray-100 bg-white px-3 py-2">
                    <div className={A.label}>Retry count</div>
                    <div className="mt-1 text-[13px] font-medium text-gray-700">
                      {event.retry_count ?? 0}
                    </div>
                  </div>
                </div>

                {event.error_message ? (
                  <div className="mt-3 min-w-0 max-w-full break-words rounded-xl border border-amber-100 bg-white px-3 py-2 text-xs text-amber-700">
                    {event.error_message}
                  </div>
                ) : null}
              </div>
            ))}

            {!loading && filtered.length === 0 ? (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-6 text-center text-[13px] text-gray-500">
                No Shopify webhook events found.
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </SectionShell>
  );
}
